import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { Reveal, Label } from "@/components/motion";
import { IMAGES, TEAM, img, useTitle } from "@/data/site";

export default function About() {
  useTitle("About | Urban Trail");

  return (
    <div data-testid="about-page">
      <PageHero
        image={IMAGES.aboutHero}
        label="About Urban Trail"
        titleLines={["Travel begins", "with a reason."]}
        tall
      />

      <section className="bg-white">
        <div className="mx-auto max-w-[1600px] px-6 py-28 lg:px-12 lg:py-40">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-3">
              <Reveal>
                <Label>Our Story</Label>
              </Reveal>
            </div>
            <div className="lg:col-span-8 lg:col-start-5">
              <Reveal>
                <p className="max-w-3xl font-serif text-3xl font-light leading-snug tracking-tight text-ink sm:text-4xl">
                  Urban Trail was created with a simple belief: travel becomes more
                  meaningful when it is thoughtfully planned.
                </p>
              </Reveal>
              <Reveal delay={0.12}>
                <p className="mt-10 max-w-2xl text-lg leading-relaxed text-graphite">
                  We are a premium travel company focused on creating journeys that
                  connect people with remarkable destinations, authentic experiences
                  and places worth remembering.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-6 max-w-2xl text-lg leading-relaxed text-graphite">
                  From the first idea to the final detail, we bring together
                  destination knowledge, thoughtful planning and personal attention to
                  create travel experiences that feel effortless, inspiring and
                  distinctly yours.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-mist bg-white">
        <div className="mx-auto max-w-[1600px] px-6 py-28 lg:px-12 lg:py-40">
          <div className="grid items-center gap-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <Label>Philosophy</Label>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="mt-6 font-serif text-4xl font-light leading-[1.02] tracking-tight text-ink sm:text-5xl">
                  Travel with intention.
                </h2>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="mt-8 text-lg leading-relaxed text-graphite">
                  We don't believe great travel is about seeing as much as possible.
                  It's about experiencing the right places in the right way.
                </p>
              </Reveal>
              <Reveal delay={0.22}>
                <p className="mt-6 text-lg leading-relaxed text-graphite">
                  We look beyond the obvious to discover landscapes, stays, cultures
                  and experiences that give every journey its own character.
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.1} className="lg:col-span-6 lg:col-start-7">
              <div className="group overflow-hidden">
                <img
                  src={IMAGES.aboutPhilosophy}
                  alt="A mountain range seen through golden pillars"
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.03]"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-ink text-white">
        <div className="mx-auto max-w-[1600px] px-6 py-28 lg:px-12 lg:py-36">
          <div className="grid gap-16 lg:grid-cols-2">
            <Reveal>
              <div data-testid="about-vision" className="border-t border-white/15 pt-10">
                <p className="label text-gold">Vision</p>
                <h2 className="mt-6 font-serif text-3xl font-light tracking-tight sm:text-4xl">Our Vision</h2>
                <p className="mt-6 max-w-md text-base leading-relaxed text-white/70">
                  To become a trusted name in premium travel, known for thoughtful
                  journeys, exceptional destinations and experiences that people
                  remember long after they return home.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div data-testid="about-mission" className="border-t border-white/15 pt-10">
                <p className="label text-gold">Mission</p>
                <h2 className="mt-6 font-serif text-3xl font-light tracking-tight sm:text-4xl">Our Mission</h2>
                <p className="mt-6 max-w-md text-base leading-relaxed text-white/70">
                  To make meaningful travel easier to discover and beautifully simple
                  to experience — through thoughtful planning, trusted partnerships and
                  a genuine understanding of every traveller we serve.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-[1600px] px-6 py-28 lg:px-12 lg:py-40">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-3">
              <Reveal>
                <Label>Direction</Label>
              </Reveal>
            </div>
            <div className="lg:col-span-8 lg:col-start-5">
              <Reveal>
                <h2 className="max-w-2xl font-serif text-4xl font-light leading-[1.02] tracking-tight text-ink sm:text-5xl">
                  Where we're going.
                </h2>
              </Reveal>
              <Reveal delay={0.12}>
                <p className="mt-8 max-w-2xl text-lg leading-relaxed text-graphite">
                  We are building Urban Trail with a long-term view: carefully
                  expanding our destinations, experiences and partnerships while
                  staying true to the principles that matter most — quality, trust,
                  personal service and thoughtful travel.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-mist bg-white">
        <div className="mx-auto max-w-[1600px] px-6 py-28 lg:px-12 lg:py-36">
          <Reveal>
            <Label>The Team</Label>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 max-w-2xl font-serif text-4xl font-light leading-[1.02] tracking-tight text-ink sm:text-5xl">
              Meet the people behind Urban Trail.
            </h2>
          </Reveal>
          {TEAM.length > 0 ? (
            <div className="mt-16 grid gap-x-10 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
              {TEAM.map((m, i) => (
                <Reveal key={m.name} delay={(i % 3) * 0.08}>
                  <div data-testid={`team-member-${i}`} className="group">
                    <div className="flex aspect-[4/5] items-center justify-center overflow-hidden bg-mist">
                      {m.photo ? (
                        <img
                          src={m.photo}
                          alt={m.name}
                          loading="lazy"
                          className="h-full w-full object-cover object-top transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
                        />
                      ) : (
                        <span className="font-serif text-6xl font-light italic text-ink/20">
                          {m.name.split(" ").map((w) => w[0]).join("")}
                        </span>
                      )}
                    </div>
                    <h3 className="mt-7 font-serif text-2xl font-light tracking-tight text-ink sm:text-3xl">
                      {m.name}
                    </h3>
                    <p className="label mt-2.5 text-gold">{m.designation}</p>
                    {m.bio && (
                      <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-steel">{m.bio}</p>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          ) : (
            <Reveal delay={0.16}>
              <div
                data-testid="team-placeholder"
                className="mt-16 border border-dashed border-mist p-12 text-center lg:p-20"
              >
                <p className="font-serif text-2xl font-light italic text-steel">
                  Team introductions are on their way.
                </p>
                <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-steel">
                  We're putting the finishing touches on this section. Until then, every
                  enquiry is answered personally by our founding team.
                </p>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      <CtaBand
        image={img.uns("photo-1558967398-d021fa33042e", 2200)}
        title="Let's write your story."
        sub="Every journey we plan begins with a conversation."
        primaryText="Plan Your Journey"
        primaryTo="/contact"
      />
    </div>
  );
}
